import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Check, Download, Loader2, ShieldCheck, Upload } from 'lucide-react';
import { CDMO_IMPORT_MESSAGES } from '../../constants/messages';
import { dbService } from '../../lib/supabase';
import { checkFileBeforeParsing, parseCdmoImportCsv } from '../../lib/cdmoImport/parseCsv';
import {
  isImportableState,
  markDuplicates,
  markFeiConflicts,
  validateRow,
  type ValidatedCdmoImportRow,
} from '../../lib/cdmoImport/validateRow';
import { importRows, type CdmoImportResult } from '../../lib/cdmoImport/importRows';
import { createExistingDomainLookup } from '../../lib/cdmoImport/existingDomains';
import { lookUpExistingProfiles } from '../../lib/cdmoImport/existingProfiles';
import { localIsoDate } from '../../lib/cdmoImport/mapToApplication';
import { buildErrorReportCsv, collectErrorReportRows, errorReportFileName } from '../../lib/cdmoImport/errorReport';
import UploadStep, { type FileRejection } from './UploadStep';
import PreviewStep from './PreviewStep';
import ResultStep from './ResultStep';
import { downloadCsv, readFileAsText } from './helpers';

type Step = 'upload' | 'preview' | 'importing' | 'result';

interface CdmoBulkUploadProps {
  /** Leaves the bulk upload: Cancel on the upload step, Done after an import. */
  onExit: () => void;
  adminEmail: string;
  adminUserId: string;
}

const STEPS: Array<{ label: string; steps: Step[] }> = [
  { label: 'Upload', steps: ['upload'] },
  { label: 'Review', steps: ['preview'] },
  { label: 'Import', steps: ['importing', 'result'] },
];

const errorText = (error: unknown) => (error instanceof Error ? error.message : String((error as { message?: unknown })?.message ?? error));

/** The duplicate lookup: the profiles whose current website is on one of the given domains. */
const findExistingByDomains = createExistingDomainLookup((domains) => dbService.findCdmoWebsiteCandidates(domains));

const StepIndicator: React.FC<{ step: Step }> = ({ step }) => {
  const current = STEPS.findIndex((s) => s.steps.includes(step));
  return (
    <>
      <p className="md:hidden text-xs font-semibold uppercase tracking-wide text-slate-gray">
        Step {current + 1} of {STEPS.length} · {STEPS[current].label}
      </p>
      <ol className="hidden md:flex items-center gap-2 shrink-0" aria-label="Import progress">
        {STEPS.map((s, index) => {
          const done = index < current || step === 'result';
          const active = index === current && step !== 'result';
          return (
            <li key={s.label} className="flex items-center gap-2" aria-current={active ? 'step' : undefined}>
              <span
                className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center ${
                  done ? 'bg-emerald-600 text-white' : active ? 'bg-electric-blue text-white' : 'bg-light-gray text-slate-gray border border-slate-gray/20'
                }`}
              >
                {done ? <Check className="w-3.5 h-3.5" aria-hidden /> : index + 1}
              </span>
              <span className={`text-sm ${active ? 'font-semibold text-charcoal' : 'text-slate-gray'}`}>{s.label}</span>
              {index < STEPS.length - 1 && <span className="w-6 h-px bg-slate-gray/30" aria-hidden />}
            </li>
          );
        })}
      </ol>
    </>
  );
};

/**
 * Admin CDMO bulk upload: CSV → validated preview → unclaimed, admin-created profiles. Rendered by
 * the `/admin/cdmo-import` page.
 *
 * Everything up to the confirmation happens in the browser; the only reads are the duplicate
 * lookups. The import creates profiles through `dbService.createCDMOApplication` (import validation
 * mode) and records every row in `cdmo_import_rows`. It sends no email.
 *
 * The confirmation and the import progress are full-screen overlays, portalled to `document.body`
 * so no transformed ancestor can trap them. While importing, the overlay also covers the site
 * header, so the admin cannot navigate away in the app mid-import; closing or reloading the tab is
 * caught by `beforeunload`.
 */
const CdmoBulkUpload: React.FC<CdmoBulkUploadProps> = ({ onExit, adminEmail, adminUserId }) => {
  const [step, setStep] = useState<Step>('upload');
  const [busy, setBusy] = useState(false);
  const [rejection, setRejection] = useState<FileRejection | null>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ValidatedCdmoImportRow[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState<CdmoImportResult | null>(null);
  const [importProblems, setImportProblems] = useState<Map<number, string>>(new Map());
  // The confirm box stays clickable while it fades out; a second click must not start a second
  // import of the same rows.
  const importInFlightRef = useRef(false);

  const importing = step === 'importing';

  // Leaving mid-import stops it part-way; the browser asks first.
  useEffect(() => {
    if (!importing) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = CDMO_IMPORT_MESSAGES.LEAVE_WARNING;
      return CDMO_IMPORT_MESSAGES.LEAVE_WARNING;
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [importing]);

  // Escape backs out of the confirmation only; it never throws away a checked file.
  useEffect(() => {
    if (!confirmOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setConfirmOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmOpen]);

  const handleFile = async (file: File) => {
    setRejection(null);
    const precheck = checkFileBeforeParsing(file);
    if (precheck) {
      setRejection({ fileName: file.name, kind: 'file', error: precheck, details: [] });
      return;
    }

    setBusy(true);
    try {
      const parsed = parseCdmoImportCsv(await readFileAsText(file));
      if (!parsed.ok) {
        setRejection({ fileName: file.name, kind: parsed.kind, error: parsed.error, details: parsed.details });
        return;
      }

      const validated = parsed.rows.map(validateRow);
      const existing = await lookUpExistingProfiles(
        { findByDomains: findExistingByDomains, findByFeiNumbers: (feiNumbers) => dbService.findCdmosByFeiNumbers(feiNumbers) },
        validated
      );

      const checked = markDuplicates(markFeiConflicts(validated, existing.feiMatches), existing.domainMatches);
      setFileName(file.name);
      setRows(checked);
      setSelected(new Set(checked.filter((row) => isImportableState(row.state)).map((row) => row.rowNumber)));
      setLookupError(existing.failure);
      setStep('preview');
    } catch (error) {
      setRejection({ fileName: file.name, kind: 'file', error: `The file could not be read: ${errorText(error)}`, details: [] });
    } finally {
      setBusy(false);
    }
  };

  const importableCount = useMemo(() => rows.filter((row) => isImportableState(row.state)).length, [rows]);
  const selectedCount = useMemo(
    () => rows.filter((row) => isImportableState(row.state) && selected.has(row.rowNumber)).length,
    [rows, selected]
  );
  const reportRows = useMemo(() => collectErrorReportRows(rows, importProblems), [rows, importProblems]);
  // Same report either way; named for what is in it.
  const reportHasErrors = reportRows.some((row) => row.errors.length > 0);

  const runImport = async () => {
    if (importInFlightRef.current) return;
    importInFlightRef.current = true;
    setConfirmOpen(false);
    setStep('importing');
    setProgress({ done: 0, total: selectedCount });
    try {
      const outcome = await importRows({
        rows,
        selected,
        context: { fileName, importedOn: localIsoDate(), adminEmail },
        batchId: crypto.randomUUID(),
        importedBy: adminUserId,
        deps: {
          findExistingByDomains,
          findExistingByFeiNumbers: (feiNumbers) => dbService.findCdmosByFeiNumbers(feiNumbers),
          createApplication: (application) => dbService.createCDMOApplication(application, { validationMode: 'import' }),
          insertAuditRows: (auditRows) => dbService.insertCdmoImportRows(auditRows),
        },
        onProgress: (done, total) => setProgress({ done, total }),
      });

      // Rows the preview called importable but the import could not create.
      const flaggedInPreview = new Set(rows.filter((row) => row.duplicate).map((row) => row.rowNumber));
      setImportProblems(
        new Map(
          outcome.results
            .filter((r) => r.outcome === 'failed' || (r.outcome === 'skipped_duplicate' && !flaggedInPreview.has(r.rowNumber)))
            .map((r) => [r.rowNumber, r.reason ?? 'Import failed'])
        )
      );
      setResult(outcome);
      setStep('result');
      if (outcome.counts.created > 0) toast.success(CDMO_IMPORT_MESSAGES.IMPORT_SUCCESS_TOAST(outcome.counts.created));
      else toast.error(CDMO_IMPORT_MESSAGES.IMPORT_NONE_TOAST);
    } catch (error) {
      console.error('CDMO bulk upload stopped unexpectedly:', error);
      toast.error(CDMO_IMPORT_MESSAGES.UNEXPECTED_IMPORT_ERROR);
      setStep('preview');
    } finally {
      importInFlightRef.current = false;
    }
  };

  const reset = () => {
    setStep('upload');
    setRows([]);
    setSelected(new Set());
    setFileName('');
    setRejection(null);
    setLookupError(null);
    setResult(null);
    setImportProblems(new Map());
  };

  const downloadReport = () => downloadCsv(buildErrorReportCsv(reportRows), errorReportFileName(fileName));

  const progressPercent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const secondaryButton =
    'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-gray/25 bg-white px-4 py-2.5 text-sm font-semibold text-charcoal hover:bg-light-gray transition-colors disabled:opacity-50';
  const primaryButton =
    'inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm';
  const overlay = 'fixed inset-0 z-[100] flex items-center justify-center p-4 bg-charcoal/40 backdrop-blur-sm';

  return (
    <>
      <section
        aria-labelledby="cdmo-bulk-upload-title"
        aria-hidden={importing || confirmOpen ? true : undefined}
        className="rounded-2xl border border-electric-blue/20 bg-white shadow-sm flex flex-col"
      >
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-6 px-5 sm:px-8 py-5 sm:py-6 border-b border-slate-gray/10">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center shadow-sm shrink-0">
              <Upload className="w-5 h-5 text-white" aria-hidden />
            </div>
            <div className="min-w-0">
              <h1 id="cdmo-bulk-upload-title" className="text-xl sm:text-2xl font-bold text-charcoal leading-tight">
                {CDMO_IMPORT_MESSAGES.PAGE_TITLE}
              </h1>
              <p className="mt-1 text-sm text-slate-gray">{CDMO_IMPORT_MESSAGES.STEP_SUBTITLES[step]}</p>
            </div>
          </div>
          <StepIndicator step={step} />
        </header>

        <div className="px-5 sm:px-8 py-6">
          {step === 'upload' && <UploadStep busy={busy} rejection={rejection} onFile={handleFile} />}

          {(step === 'preview' || step === 'importing') && (
            <PreviewStep
              fileName={fileName}
              rows={rows}
              selected={selected}
              onSelectedChange={setSelected}
              lookupError={lookupError}
              onChooseAnotherFile={reset}
            />
          )}

          {step === 'result' && result && <ResultStep result={result} />}
        </div>

        {step !== 'importing' && (
          // Stays in view while a long preview scrolls (not on phones, where the stacked buttons would
          // cover too much of the screen).
          <footer className="sm:sticky sm:bottom-0 z-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-5 sm:px-8 py-4 border-t border-slate-gray/10 bg-light-gray/95 backdrop-blur-sm rounded-b-2xl">
            <p className="text-sm text-slate-gray">
              {step === 'preview' && importableCount === 0 && CDMO_IMPORT_MESSAGES.NOTHING_TO_IMPORT}
              {step === 'preview' && importableCount > 0 && (
                <>
                  <span className="font-semibold text-charcoal">{selectedCount}</span> of {importableCount} importable row
                  {importableCount === 1 ? '' : 's'} selected
                </>
              )}
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              {step === 'upload' && (
                <button type="button" onClick={onExit} className={secondaryButton}>
                  Cancel
                </button>
              )}
              {(step === 'preview' || step === 'result') && reportRows.length > 0 && (
                <button type="button" onClick={downloadReport} className={secondaryButton}>
                  <Download className="w-4 h-4" aria-hidden />
                  {reportHasErrors ? CDMO_IMPORT_MESSAGES.ERROR_REPORT_BUTTON : CDMO_IMPORT_MESSAGES.WARNINGS_REPORT_BUTTON}
                </button>
              )}
              {step === 'preview' && (
                <button type="button" onClick={() => setConfirmOpen(true)} disabled={selectedCount === 0} className={primaryButton}>
                  {CDMO_IMPORT_MESSAGES.CREATE_BUTTON(selectedCount)}
                </button>
              )}
              {step === 'result' && (
                <>
                  <button type="button" onClick={reset} className={secondaryButton}>
                    {CDMO_IMPORT_MESSAGES.UPLOAD_ANOTHER}
                  </button>
                  <button type="button" onClick={onExit} className={primaryButton}>
                    {CDMO_IMPORT_MESSAGES.DONE}
                  </button>
                </>
              )}
            </div>
          </footer>
        )}
      </section>

      {createPortal(
        <AnimatePresence>
          {confirmOpen && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={overlay}
              onClick={() => setConfirmOpen(false)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 12 }}
                onClick={(e) => e.stopPropagation()}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="cdmo-bulk-upload-confirm-title"
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-slate-gray/10"
              >
                <div className="w-11 h-11 rounded-xl bg-electric-blue/10 flex items-center justify-center mb-4">
                  <ShieldCheck className="w-6 h-6 text-selected-blue" aria-hidden />
                </div>
                <h2 id="cdmo-bulk-upload-confirm-title" className="text-lg font-bold text-charcoal">
                  {CDMO_IMPORT_MESSAGES.CONFIRM_TITLE(selectedCount)}
                </h2>
                <ul className="mt-3 space-y-2 text-sm text-slate-gray">
                  {CDMO_IMPORT_MESSAGES.CONFIRM_POINTS.map((point) => (
                    <li key={point} className="flex gap-2">
                      <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
                  <button type="button" onClick={() => setConfirmOpen(false)} className={secondaryButton}>
                    Cancel
                  </button>
                  <button type="button" onClick={runImport} className={primaryButton}>
                    {CDMO_IMPORT_MESSAGES.CONFIRM_ACTION(selectedCount)}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {importing && (
            <motion.div key="importing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={overlay}>
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="cdmo-bulk-upload-importing-title"
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full px-6 py-10 border border-slate-gray/10"
              >
                <div className="flex flex-col items-center text-center" role="status" aria-live="polite">
                  <Loader2 className="w-10 h-10 text-electric-blue animate-spin mb-4" aria-hidden />
                  <h2 id="cdmo-bulk-upload-importing-title" className="text-lg font-semibold text-charcoal">
                    {CDMO_IMPORT_MESSAGES.IMPORTING_TITLE}
                  </h2>
                  <p className="mt-1 text-sm text-slate-gray tabular-nums">
                    {progress.done} of {progress.total}
                  </p>
                  <div
                    className="mt-5 w-full h-2.5 rounded-full bg-light-gray overflow-hidden"
                    role="progressbar"
                    aria-label="Profiles created"
                    aria-valuemin={0}
                    aria-valuemax={progress.total}
                    aria-valuenow={progress.done}
                  >
                    <div className="h-full bg-gradient-primary transition-all duration-300" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <p className="mt-4 text-xs text-slate-gray">{CDMO_IMPORT_MESSAGES.IMPORTING_HINT}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default CdmoBulkUpload;
